import { Alert, Box, Divider, Typography } from "@mui/material";
import { FieldValues } from "react-hook-form";
import * as Yup from "yup";

import { AttachmentView } from "../../elements/attachment-view";
import { FormType } from "../components";
import { FieldToolsWrapper } from "../components/react-hook-form/wrapper/field-tools";
import {
  FormAsyncCheckBoxTable,
  FormAsyncMultiSelect,
  FormCheckBox,
  FormCheckBoxGroup,
  FormDate,
  FormFile,
  FormNumber,
  FormPublicUploader,
  FormRadioGroup,
  FormSelect,
  FormText,
} from "../fields";
import FormikAsyncSelect from "../fields/async-select";
import { LexicalEditor } from "../fields/lexical";
import { FormMultiSelect } from "../fields/multi-select";
import { MULTI_SELECT_FIELD_TYPES } from "../constants";

import { INITIAL_EDITOR_VALUE_LENGTH } from "@/components/elements/lexical/constants";
import { FieldSchema, FormFieldSchema } from "@/components/section";

function generateFieldInitialValue<TFormValues extends FieldValues, TRecord>(
  defaultValue: FormFieldSchema<TFormValues, TRecord>["defaultValue"],
  fieldSchema: FieldSchema<TFormValues, TRecord>
) {
  return "defaultValue" in fieldSchema && fieldSchema.defaultValue !== undefined
    ? fieldSchema.defaultValue
    : defaultValue;
}

function generateRequiredValidationSchema<TFormValues extends FieldValues, TRecord>(
  schema: Yup.AnySchema,
  fieldType: FieldSchema<TFormValues, TRecord>["type"]
) {
  const schemaWithRequired = schema.required("Required");

  if (MULTI_SELECT_FIELD_TYPES.includes(fieldType)) {
    return schemaWithRequired.min(1, "Required"); // For array fields, at least one value is required
  }

  return schemaWithRequired;
}

function generateFieldValidationSchema<TFormValues extends FieldValues, TRecord>(
  defaultSchema: Yup.AnySchema,
  fieldSchema: FieldSchema<TFormValues, TRecord>
) {
  const schema = "schema" in fieldSchema && fieldSchema.schema ? fieldSchema.schema : defaultSchema;
  return "required" in fieldSchema && fieldSchema.required === true
    ? generateRequiredValidationSchema(schema, fieldSchema.type)
    : schema;
}

export function generateFieldDefaults<TFormValues extends FieldValues, TRecord>(
  key: string,
  fieldSchema: FieldSchema<TFormValues, TRecord>,
  formType: FormType = "create"
) {
  switch (fieldSchema.type) {
    case "async-check-box-table": {
      return {
        schema: generateFieldValidationSchema(Yup.array().of(Yup.string()), fieldSchema),
        initialValue: generateFieldInitialValue([], fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormAsyncCheckBoxTable {...props} />}
          />
        ),
      };
    }

    case "async-multi-select": {
      return {
        schema: generateFieldValidationSchema(Yup.array().of(Yup.mixed()), fieldSchema),
        initialValue: generateFieldInitialValue([], fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormAsyncMultiSelect {...props} />}
          />
        ),
      };
    }

    case "async-select": {
      return {
        schema: generateFieldValidationSchema(Yup.mixed().nullable(), fieldSchema),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormikAsyncSelect {...props} />}
          />
        ),
      };
    }

    case "check-box": {
      return {
        schema: generateFieldValidationSchema(Yup.boolean(), fieldSchema),
        initialValue: generateFieldInitialValue(false, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormCheckBox {...props} />}
          />
        ),
      };
    }

    case "check-box-group": {
      return {
        schema: generateFieldValidationSchema(Yup.array().of(Yup.string()), fieldSchema),
        initialValue: generateFieldInitialValue([], fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormCheckBoxGroup {...props} />}
          />
        ),
      };
    }

    case "date": {
      return {
        schema: generateFieldValidationSchema(
          Yup.string()
            .nullable()
            .test("is-valid-date", "Invalid date", (value) => (!value ? true : !value.includes("Invalid"))),
          fieldSchema
        ),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormDate {...props} />}
          />
        ),
      };
    }

    case "number": {
      return {
        schema: generateFieldValidationSchema(Yup.number().nullable(), fieldSchema),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormNumber {...props} />}
          />
        ),
      };
    }

    case "file": {
      return {
        schema: generateFieldValidationSchema(Yup.number().nullable(), fieldSchema),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormFile {...props} />}
          />
        ),
      };
    }

    case "file-public": {
      return {
        schema: generateFieldValidationSchema(
          Yup.array().of(
            Yup.object().shape({
              name: Yup.string(),
              url: Yup.string(),
            })
          ),
          fieldSchema
        ),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormPublicUploader {...props} />}
          />
        ),
      };
    }

    case "radio-group": {
      return {
        schema: generateFieldValidationSchema(Yup.mixed().nullable(), fieldSchema),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            renderField={(props) => <FormRadioGroup {...props} />}
          />
        ),
      };
    }

    case "select": {
      return {
        schema: generateFieldValidationSchema(Yup.mixed().nullable(), fieldSchema),
        initialValue: generateFieldInitialValue(null, fieldSchema),
        element: (
          <FieldToolsWrapper key={key} fieldSchema={fieldSchema} renderField={(props) => <FormSelect {...props} />} />
        ),
      };
    }

    case "multi-select": {
      return {
        schema: generateFieldValidationSchema(Yup.array().of(Yup.mixed()), fieldSchema),
        initialValue: generateFieldInitialValue([], fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            renderField={(props) => <FormMultiSelect {...props} />}
          />
        ),
      };
    }

    case "text": {
      return {
        schema: generateFieldValidationSchema(
          "multiline" in fieldSchema
            ? Yup.string().nullable()
            : Yup.string().nullable().max(255, "Cannot exceed ${max} characters"),
          fieldSchema
        ),
        initialValue: generateFieldInitialValue("", fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <FormText {...props} />}
          />
        ),
      };
    }

    case "lexical": {
      return {
        schema: generateFieldValidationSchema(
          Yup.string().min(INITIAL_EDITOR_VALUE_LENGTH, "Must not be empty"),
          fieldSchema
        ),
        initialValue: generateFieldInitialValue("", fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            formType={formType}
            renderField={(props) => <LexicalEditor {...props} initialState={props.initialState as string} />}
          />
        ),
      };
    }

    /**
     * Non-react-hook-query fields
     */
    case "attachment-view": {
      return {
        initialValue: generateFieldInitialValue([], fieldSchema),
        element: (
          <FieldToolsWrapper
            key={key}
            fieldSchema={fieldSchema}
            renderField={(props) => <AttachmentView {...props} />}
          />
        ),
      };
    }

    case "typography": {
      return {
        element: (
          <FieldToolsWrapper key={key} fieldSchema={fieldSchema} renderField={(props) => <Typography {...props} />} />
        ),
      };
    }
    case "alert": {
      return {
        element: (
          <FieldToolsWrapper key={key} fieldSchema={fieldSchema} renderField={(props) => <Alert {...props} />} />
        ),
      };
    }
    case "divider": {
      return {
        element: (
          <FieldToolsWrapper key={key} fieldSchema={fieldSchema} renderField={(props) => <Divider {...props} />} />
        ),
      };
    }
    case "box": {
      return {
        element: <FieldToolsWrapper key={key} fieldSchema={fieldSchema} renderField={(props) => <Box {...props} />} />,
      };
    }

    default:
      return {
        schema: Yup.mixed(),
        initialValue: null,
        element: null,
      };
  }
}
